<template>
  <div class="space-y-4">
    <!-- Config Bar -->
    <div class="bg-white rounded-xl border border-gray-200 p-4 flex items-end gap-4">
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Performance Year</label>
        <select :value="targetPy" @change="$emit('update:target-py', Number($event.target.value))" class="border border-gray-200 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-300">
          <option :value="2026">PY 2026</option>
          <option :value="2025">PY 2025</option>
          <option :value="2024">PY 2024</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">Data Year</label>
        <select :value="year" @change="$emit('update:year', Number($event.target.value))" class="border border-gray-200 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-300">
          <option :value="2024">2024</option>
          <option :value="2023">2023</option>
          <option :value="2022">2022</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">MSSP Track</label>
        <select :value="track" @change="$emit('update:track', $event.target.value)" class="border border-gray-200 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-300">
          <option value="BASIC_A">BASIC A</option>
          <option value="BASIC_B">BASIC B</option>
          <option value="BASIC_C">BASIC C</option>
          <option value="BASIC_D">BASIC D</option>
          <option value="BASIC_E">BASIC E</option>
          <option value="ENHANCED">ENHANCED</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1">MSR/MLR</label>
        <select :value="msrType" @change="$emit('update:msr-type', $event.target.value)" class="border border-gray-200 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-300">
          <option value="variable">Variable</option>
          <option value="0">0% (Zero)</option>
          <option value="0.5">0.5% Fixed</option>
          <option value="1.0">1.0% Fixed</option>
          <option value="1.5">1.5% Fixed</option>
          <option value="2.0">2.0% Fixed</option>
        </select>
      </div>
      <div class="flex items-end">
        <label class="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded cursor-pointer text-sm" :class="qualityMet ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'">
          <input type="checkbox" :checked="qualityMet" @change="$emit('update:quality-met', $event.target.checked)" class="rounded text-primary-600 focus:ring-primary-300" />
          <span :class="qualityMet ? 'text-emerald-700' : 'text-red-700'">{{ qualityMet ? 'Quality met' : 'Quality failed' }}</span>
        </label>
      </div>
      <div class="flex-1"></div>
      <button v-if="roster.length > 0 && !loading" @click="$emit('calculate')" class="px-5 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-600-hover transition-colors ">
        Calculate Projection
      </button>
      <ExportMenu size="lg" :disabled="roster.length === 0" :exports="[{ type: 'csv', action: () => $emit('export') }]" />
    </div>

    <!-- Main Layout -->
    <div class="flex gap-4" style="min-height: 500px;">
      <!-- Left: Roster (1/3) -->
      <div class="w-1/3">
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden h-full flex flex-col">
          <div class="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <span class="text-sm font-semibold text-gray-900">Roster ({{ roster.length }} TINs)</span>
            <button v-if="roster.length > 0" @click="$emit('clear')" class="text-xs text-red-600 hover:underline">Clear all</button>
          </div>
          <div v-if="roster.length === 0" class="flex-1 flex items-center justify-center p-8">
            <div class="text-center">
              <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              <p class="text-sm text-gray-500">Add practices from the Practices tab</p>
              <p class="text-xs text-gray-500 mt-1">Click the + button on any practice with a linked TIN</p>
            </div>
          </div>
          <div v-else class="flex-1 overflow-y-auto divide-y divide-gray-100">
            <div v-for="item in roster" :key="item.tinId" class="flex justify-between items-center px-4 py-2.5 group hover:bg-gray-50 transition-colors">
              <div class="truncate flex-1 min-w-0">
                <div class="font-medium text-sm text-gray-900 truncate">{{ item.name }}</div>
                <div class="text-xs text-gray-500">{{ item.tinId }}</div>
              </div>
              <button @click="$emit('remove', item.tinId)" class="text-gray-500 hover:text-red-600 ml-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Results (2/3) -->
      <div class="w-2/3">
        <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <svg class="animate-spin h-8 w-8 mx-auto mb-3 text-primary-600" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <p class="text-gray-500">Calculating projection...</p>
        </div>

        <div v-else-if="error" class="bg-white rounded-xl border border-red-200 p-6">
          <div class="text-red-600 font-medium mb-1">Projection Error</div>
          <div class="text-sm text-gray-500">{{ error }}</div>
        </div>

        <div v-else-if="warning" class="bg-white rounded-xl border border-amber-200 p-6">
          <div class="text-amber-700 font-medium mb-1">Warning</div>
          <div class="text-sm text-amber-600">{{ warning }}</div>
        </div>

        <div v-else-if="!projection" class="bg-gray-50 rounded-xl border border-gray-200 p-12 text-center h-full flex items-center justify-center">
          <div>
            <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
            <p class="text-gray-500" v-if="roster.length === 0">Add practices to your roster, then calculate a projection</p>
            <p class="text-gray-500" v-else>Click <strong>Calculate Projection</strong> to analyze your roster</p>
          </div>
        </div>

        <!-- Results with 3 tabs -->
        <div v-else class="space-y-4">
          <div class="flex gap-1 bg-gray-50 rounded-lg p-1 w-fit">
            <button @click="activeView = 'projection'" :class="tabClass('projection')">ACO Projection</button>
            <button @click="activeView = 'peers'" :class="tabClass('peers')">Peer Benchmark</button>
            <button @click="activeView = 'model'" :class="tabClass('model')">Model</button>
          </div>

          <!-- ACO Projection tab -->
          <template v-if="activeView === 'projection'">
            <AcoProjectionSummary :projection="projection" />
          </template>

          <!-- Peer Benchmark tab -->
          <template v-else-if="activeView === 'peers'">
            <!-- Roster / single TIN benchmark -->
            <PracticePeerBenchmark
              :data="peerData || projection.peer_comparison"
              :loading="peerLoading"
              :selected-tin="selectedPeerTin"
              @select-tin="selectedPeerTin = selectedPeerTin === $event ? null : $event"
              @update-dimensions="refetchPeers"
            />

            <!-- Selected TIN drill-down (separate instance below) -->
            <div v-if="selectedPeerTin" class="mt-4">
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ selectedPeerTinName }}</span>
                <button @click="selectedPeerTin = null" class="text-xs text-gray-500 hover:text-gray-900">Close</button>
              </div>
              <PracticePeerBenchmark
                :data="selectedPeerTinData"
                :loading="selectedPeerTinLoading"
                @update-dimensions="refetchSelectedTin"
              />
            </div>
          </template>

          <!-- Model tab -->
          <template v-else>
            <AcoModelView :projection="projection" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PracticePeerBenchmark from '../modules/PracticePeerBenchmark.vue'
import AcoModelView from '../modules/AcoModelView.vue'
import AcoProjectionSummary from '../modules/AcoProjectionSummary.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

export default {
  name: 'AcoBuilderTab',
  components: { PracticePeerBenchmark, AcoModelView, AcoProjectionSummary, ExportMenu },
  props: {
    roster: { type: Array, required: true },
    year: { type: Number, required: true },
    track: { type: String, required: true },
    msrType: { type: String, default: 'variable' },
    qualityMet: { type: Boolean, default: true },
    targetPy: { type: Number, default: 2026 },
    projection: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    warning: { type: String, default: null },
  },
  emits: ['remove', 'clear', 'update:year', 'update:track', 'update:msr-type', 'update:quality-met', 'update:target-py', 'calculate', 'export'],
  data() {
    return {
      activeView: 'projection',
      peerData: null,
      peerLoading: false,
      selectedPeerTin: null,
      selectedPeerTinData: null,
      selectedPeerTinLoading: false,
    }
  },
  watch: {
    projection() {
      this.peerData = null
      this.selectedPeerTin = null
      this.selectedPeerTinData = null
    },
    selectedPeerTin(tinId) {
      if (tinId) this.fetchTinBenchmark(tinId)
      else this.selectedPeerTinData = null
    },
  },
  computed: {
    selectedPeerTinName() {
      if (!this.selectedPeerTin || !this.projection?.peer_comparison?.per_tin) return ''
      const t = this.projection.peer_comparison.per_tin.find(t => t.tin_id === this.selectedPeerTin)
      return t?.name || this.selectedPeerTin
    },
  },
  methods: {
    tabClass(tab) {
      return [
        'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
        this.activeView === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900',
      ]
    },
    async refetchPeers(activeDimensions) {
      if (!this.roster.length) return
      this.peerLoading = true
      try {
        const tinId = this.roster[0].tinId
        const dims = activeDimensions.join(',')
        const response = await this.$axios.get(`/api/growth/practices/${tinId}/peer-benchmark/?dimensions=${dims}`)
        this.peerData = response.data
      } catch (err) {
        console.error('Failed to refetch peer benchmark', err)
      } finally {
        this.peerLoading = false
      }
    },
    async fetchTinBenchmark(tinId, dimensions) {
      this.selectedPeerTinLoading = true
      if (!dimensions) this.selectedPeerTinData = null
      try {
        let url = `/api/growth/practices/${tinId}/peer-benchmark/`
        if (dimensions) url += `?dimensions=${dimensions.join(',')}`
        const response = await this.$axios.get(url)
        this.selectedPeerTinData = response.data
      } catch (err) {
        console.error('Failed to load TIN benchmark', err)
      } finally {
        this.selectedPeerTinLoading = false
      }
    },
    refetchSelectedTin(activeDimensions) {
      if (this.selectedPeerTin) {
        this.fetchTinBenchmark(this.selectedPeerTin, activeDimensions)
      }
    },
  },
}
</script>
